/**
 * Zod schema for Qualitas AML questionnaire (Zod v4).
 */
import { z } from 'zod'
import {
  AML_ECONOMIC_ACTIVITIES,
  AML_FUND_ORIGINS,
  AML_GENDERS,
  AML_INCOME_RANGES,
  type AmlFormValues,
} from '../types/aml.types'

const incomeTuple = AML_INCOME_RANGES as unknown as [string, ...string[]]
const fundTuple = AML_FUND_ORIGINS as unknown as [string, ...string[]]
const genderTuple = AML_GENDERS as unknown as [string, ...string[]]
const activityTuple = AML_ECONOMIC_ACTIVITIES as unknown as [string, ...string[]]

export const amlFormSchema = z.object({
  fullName: z.string().trim().min(2, 'aml.error_full_name').max(200, 'aml.error_full_name'),
  phone: z.string().trim().min(7, 'aml.error_phone').max(32, 'aml.error_phone'),
  dateOfBirth: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'aml.error_date_of_birth'),
  gender: z.enum(genderTuple, { message: 'aml.error_gender' }),
  // countries.id is ULID text (not uuid)
  nationalityCountryId: z.string().min(1, 'aml.error_nationality'),
  originCountryId: z.string().min(1).nullable(),
  residenceCountryId: z.string().min(1, 'aml.error_residence'),
  incomeRange: z.enum(incomeTuple, { message: 'aml.error_income' }),
  // Closed catalogue, not prose: the insurer maps this onto a code list, and a
  // free-text answer is unmappable by construction.
  economicActivity: z.enum(activityTuple, { message: 'aml.error_economic_activity' }),
  fundOrigin: z.enum(fundTuple, { message: 'aml.error_fund_origin' }),
  isPep: z.boolean({ message: 'aml.error_required' }),
  isPoliticalPartyMember: z.boolean({ message: 'aml.error_required' }),
  hasPepFamily: z.boolean({ message: 'aml.error_required' }),
  hasPepAssociate: z.boolean({ message: 'aml.error_required' }),
  hasTaxHavenRelations: z.boolean({ message: 'aml.error_required' }),
  hasIllicitOrJudicialHistory: z.boolean({ message: 'aml.error_required' }),
  disclaimerAccepted: z.literal(true, { message: 'aml.error_disclaimer' }),
  disclaimerScrollCompleted: z.literal(true, { message: 'aml.error_disclaimer_scroll' }),
  // Art. 44 D.L. 144/2024: the international transfer needs its own prior and
  // express consent. Same shape as the disclaimer, never the same field — one
  // affirmative act may only authorize one thing.
  crossBorderTransferAccepted: z.literal(true, { message: 'aml.error_cross_border' }),
})

export function emptyAmlFormValues(partial?: Partial<AmlFormValues>): AmlFormValues {
  return {
    fullName: partial?.fullName ?? '',
    phone: partial?.phone ?? '',
    dateOfBirth: partial?.dateOfBirth ?? '',
    gender: partial?.gender ?? null,
    nationalityCountryId: partial?.nationalityCountryId ?? null,
    originCountryId: partial?.originCountryId ?? null,
    residenceCountryId: partial?.residenceCountryId ?? null,
    incomeRange: partial?.incomeRange ?? null,
    economicActivity: partial?.economicActivity ?? null,
    fundOrigin: partial?.fundOrigin ?? null,
    isPep: partial?.isPep ?? null,
    isPoliticalPartyMember: partial?.isPoliticalPartyMember ?? null,
    hasPepFamily: partial?.hasPepFamily ?? null,
    hasPepAssociate: partial?.hasPepAssociate ?? null,
    hasTaxHavenRelations: partial?.hasTaxHavenRelations ?? null,
    hasIllicitOrJudicialHistory: partial?.hasIllicitOrJudicialHistory ?? null,
    disclaimerAccepted: partial?.disclaimerAccepted ?? false,
    disclaimerScrollCompleted: partial?.disclaimerScrollCompleted ?? false,
    // Defaults false and no caller seeds it: a pre-ticked box is not consent,
    // and a consent granted on a previous submission is not consent for this
    // one (`buildFormValues` deliberately omits it in every branch).
    crossBorderTransferAccepted: partial?.crossBorderTransferAccepted ?? false,
  }
}
